import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminStats } from '../../hooks/useAdminStats';
import { useStripeStats } from '../../hooks/useStripeStats';
import {
  AlertCircle,
  Crown,
  DollarSign,
  Layers,
  RefreshCw,
  Shield,
  Users,
  CreditCard,
  TrendingUp,
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const { stats, loading, error, refetch } = useAdminStats();
  const { stats: stripeStats, loading: stripeLoading, error: stripeError, refetch: refetchStripe } = useStripeStats();

  if (!user) {
    return (
      <div className="space-y-4 p-4">
        <div className="rounded-3xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-8 text-center shadow-xl dark:theme-card-bg dark:theme-card-border">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-[rgb(var(--fg))]">
            Accès non autorisé
          </h2>
          <p className="text-gray-600 dark:text-[rgb(var(--muted))]">
            Vous devez être connecté pour accéder au tableau de bord administrateur.
          </p>
        </div>
      </div>
    );
  }

  const totalUsers = stats?.totals.allTimeUsers ?? 0;
  const newUsersInRange = stats?.totals.newUsersInRange ?? 0;
  const payingSubscriptions = stats?.subscriptions.total ?? 0;
  const activePayingSubscriptions = stats?.subscriptions.active ?? 0;
  const monthlyRevenue = stats?.subscriptions.monthlyRevenue ?? 0;
  const yearlyRevenue = stats?.subscriptions.yearlyRevenue ?? 0;
  const currency = (stats?.subscriptions.currency || 'USD').toUpperCase();

  const planSummaries = React.useMemo(() => {
    if (!stats?.plans) return [];

    const labels: Record<string, string> = {
      free: 'Plan Gratuit',
      launch: 'Plan Launch',
      lunch: 'Plan Lunch',
      boost: 'Plan Boost',
      scale: 'Plan Scale',
      pro: 'Plan Pro',
    };

    const entries = Array.isArray(stats.plans)
      ? stats.plans
      : Object.entries(stats.plans as Record<string, unknown>).map(([key, value]) => {
          if (value && typeof value === 'object') {
            const plan = value as Record<string, unknown>;
            const planName = (plan.name as string) || key;
            const planDisplay = (plan.displayName as string) || (plan.label as string) || labels[planName.toLowerCase()] || planName;
            const planCount = Number(plan.count ?? plan.total ?? plan.value ?? 0);
            return { name: planName, displayName: planDisplay, count: planCount };
          }

          const numericCount = Number(value ?? 0);
          return {
            name: key,
            displayName: labels[key.toLowerCase()] || key,
            count: Number.isFinite(numericCount) ? numericCount : 0,
          };
        });

    const normalized = entries
      .map((plan) => {
        if (!plan) return null;
        const name = (plan as any).name ? String((plan as any).name) : '';
        if (!name) return null;

        const countRaw = (plan as any).count ?? 0;
        const count = Number.isFinite(Number(countRaw)) ? Number(countRaw) : 0;
        const displayName = (plan as any).displayName || labels[name.toLowerCase()] || name;

        return {
          name,
          displayName,
          count,
        };
      })
      .filter((plan): plan is { name: string; displayName: string; count: number } => Boolean(plan));

    if (normalized.length === 0) {
      return [];
    }

    const order = ['launch', 'lunch', 'free', 'boost', 'scale', 'pro'];
    return normalized.sort((a, b) => {
      const indexA = order.indexOf(a.name.toLowerCase());
      const indexB = order.indexOf(b.name.toLowerCase());
      const scoreA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const scoreB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return scoreA - scoreB;
    });
  }, [stats?.plans]);

  const isFreePlan = (name: string) => ['launch', 'lunch', 'free'].includes(name.toLowerCase());
  const freePlanSummary = planSummaries.find((plan) => isFreePlan(plan.name));
  const paidPlanSummaries = planSummaries.filter((plan) => !isFreePlan(plan.name));
  const freePlanCount = freePlanSummary?.count ?? Math.max(totalUsers - payingSubscriptions, 0);
  const paidPlanCount = paidPlanSummaries.reduce((sum, plan) => sum + plan.count, 0);

  const planPayingShare =
    paidPlanCount + freePlanCount > 0
      ? ((paidPlanCount / (paidPlanCount + freePlanCount)) * 100).toFixed(1)
      : '0.0';

  const formatCurrencyValue = (amount: number, currencyCode = currency) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(amount);

  const getPlanDotClass = (name: string) => {
    const key = name.toLowerCase();
    if (['launch', 'lunch', 'free'].includes(key)) return 'bg-slate-400';
    if (key === 'boost') return 'bg-purple-500';
    if (key === 'scale') return 'bg-emerald-500';
    return 'bg-[rgb(var(--brand))]';
  };

  const renderValue = (value: number) => value.toLocaleString('fr-FR');

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#7c3aed] via-[#6366f1] to-[#22d3ee]">
            <Shield className="h-5 w-5 text-[rgb(var(--fg))]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-[rgb(var(--fg))]">
              Administration
            </h1>
            <p className="text-sm text-gray-600 dark:text-[rgb(var(--muted))]">
              Vue d’ensemble simplifiée de la plateforme
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            refetch();
            refetchStripe();
          }}
          className="inline-flex items-center gap-2 rounded-full border theme-btn-secondary px-4 py-2 text-sm font-medium transition-transform hover:scale-105"
          disabled={loading || stripeLoading}
        >
          <RefreshCw className={`h-4 w-4 ${loading || stripeLoading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </button>
      </div>

      {error && (
        <div className="rounded-3xl border border-red-200 bg-red-50/60 p-4 text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {loading && !stats && (
        <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-10 text-center text-sm theme-text-secondary">
          Chargement des statistiques...
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-lg transition-shadow hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary">
                  Utilisateurs inscrits
                </p>
                <p className="mt-3 text-3xl font-semibold theme-text-primary">
                  {renderValue(totalUsers)}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-100 p-3 dark:bg-slate-700/40">
                <Users className="h-5 w-5 text-slate-600 dark:text-[rgb(var(--muted))]" />
              </div>
            </div>
            <p className="mt-4 text-xs theme-text-secondary">
              {renderValue(newUsersInRange)} nouveaux utilisateurs sur la période suivie.
            </p>
          </div>

          <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-lg transition-shadow hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary">
                  Abonnements payants
                </p>
                <p className="mt-3 text-3xl font-semibold theme-text-primary">
                  {renderValue(payingSubscriptions)}
                </p>
              </div>
              <div className="rounded-2xl bg-purple-100 p-3 dark:bg-purple-900/30">
                <Crown className="h-5 w-5 text-purple-500 dark:text-purple-300" />
              </div>
            </div>
            <p className="mt-4 text-xs theme-text-secondary">
              {activePayingSubscriptions === 0
                ? 'Aucun abonnement payant actif pour le moment.'
                : paidPlanSummaries.map((plan) => `${plan.displayName}: ${renderValue(plan.count)}`).join(' • ')}
            </p>
          </div>

          <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-lg transition-shadow hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary">
                  Répartition des plans
                </p>
                <div className="mt-3 space-y-2 text-sm theme-text-secondary">
                  {planSummaries.length === 0 ? (
                    <p className="text-xs theme-text-tertiary">Aucune donnée de plan disponible.</p>
                  ) : (
                    planSummaries.map((plan) => (
                      <div key={plan.name} className="flex items-center justify-between gap-4">
                        <span className="inline-flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${getPlanDotClass(plan.name)}`} />
                          {plan.displayName}
                        </span>
                        <span className="font-semibold theme-text-primary">{renderValue(plan.count)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-100 p-3 dark:bg-slate-700/40">
                <Layers className="h-5 w-5 text-slate-600 dark:text-[rgb(var(--muted))]" />
              </div>
            </div>
            <p className="mt-4 text-xs theme-text-secondary">
              {planSummaries.length > 0
                ? `${planPayingShare}% des comptes sont sur un plan payant.`
                : 'Aucune donnée de plan disponible.'}
            </p>
          </div>

          <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-lg transition-shadow hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary">
                  Revenus des abonnements
                </p>
                <p className="mt-3 text-3xl font-semibold theme-text-primary">
                  {formatCurrencyValue(monthlyRevenue)}
                </p>
                <p className="mt-1 text-xs theme-text-secondary">Facturation mensuelle</p>
                <div className="mt-4 space-y-2 text-xs theme-text-secondary">
                  <div className="flex items-center justify-between">
                    <span>Annuel (prévision)</span>
                    <span className="font-semibold theme-text-primary">
                      {formatCurrencyValue(yearlyRevenue)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Monnaie</span>
                    <span className="font-semibold theme-text-primary">{currency}</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl bg-emerald-100 p-3 dark:bg-emerald-900/30">
                <DollarSign className="h-5 w-5 text-emerald-500 dark:text-emerald-300" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Statistics */}
      <div className="rounded-3xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-lg">
        <div className="flex items-center gap-4 mb-6">
          <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 p-3">
            <DollarSign className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold theme-text-primary">
              Statistiques Stripe
            </h2>
            <p className="text-sm theme-text-secondary mt-1">
              Données en temps réel depuis votre compte Stripe
            </p>
          </div>
        </div>

        {stripeError && (
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-4 text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 mb-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">{stripeError}</p>
            </div>
          </div>
        )}

        {stripeLoading ? (
          <div className="text-center py-8 text-sm theme-text-secondary">
            Chargement des statistiques Stripe...
          </div>
        ) : stripeStats ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Abonnements actifs */}
            <div className="rounded-2xl border theme-card-border bg-[rgb(var(--card))] p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="rounded-xl bg-purple-100 p-2.5 dark:bg-purple-900/30">
                  <Crown className="h-5 w-5 text-purple-500 dark:text-purple-300" />
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary mb-1">
                Abonnements actifs
              </p>
              <p className="text-2xl font-semibold theme-text-primary mb-1">
                {renderValue(stripeStats.subscriptions.active)}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs theme-text-secondary">
                <span>Total: {renderValue(stripeStats.subscriptions.total)}</span>
                {stripeStats.subscriptions.trialing > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    En essai: {renderValue(stripeStats.subscriptions.trialing)}
                  </span>
                )}
              </div>
            </div>

            {/* Clients */}
            <div className="rounded-2xl border theme-card-border bg-[rgb(var(--card))] p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="rounded-xl bg-blue-100 p-2.5 dark:bg-blue-900/30">
                  <Users className="h-5 w-5 text-blue-500 dark:text-blue-300" />
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary mb-1">
                Clients Stripe
              </p>
              <p className="text-2xl font-semibold theme-text-primary">
                {renderValue(stripeStats.customers.total)}
              </p>
            </div>

            {/* Revenus mensuels */}
            <div className="rounded-2xl border theme-card-border bg-[rgb(var(--card))] p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="rounded-xl bg-emerald-100 p-2.5 dark:bg-emerald-900/30">
                  <TrendingUp className="h-5 w-5 text-emerald-500 dark:text-emerald-300" />
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary mb-1">
                Revenus mensuels (MRR)
              </p>
              <p className="text-2xl font-semibold theme-text-primary mb-1">
                {formatCurrencyValue(stripeStats.revenue.monthly)}
              </p>
              <p className="text-xs theme-text-secondary mt-2">
                Annuel: {formatCurrencyValue(stripeStats.revenue.yearly)}
              </p>
            </div>

            {/* Paiements récents */}
            <div className="rounded-2xl border theme-card-border bg-[rgb(var(--card))] p-5 shadow-md">
              <div className="flex items-center justify-between mb-3">
                <div className="rounded-xl bg-indigo-100 p-2.5 dark:bg-indigo-900/30">
                  <CreditCard className="h-5 w-5 text-indigo-500 dark:text-indigo-300" />
                </div>
              </div>
              <p className="text-xs font-medium uppercase tracking-wide theme-text-tertiary mb-1">
                Revenus récents (30j)
              </p>
              <p className="text-2xl font-semibold theme-text-primary mb-1">
                {formatCurrencyValue(stripeStats.revenue.recent)}
              </p>
              <div className="flex items-center gap-4 mt-3 text-xs theme-text-secondary">
                <span className="text-emerald-600 dark:text-emerald-400">
                  Réussis: {renderValue(stripeStats.payments.succeeded)}
                </span>
                {stripeStats.payments.pending > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    En attente: {renderValue(stripeStats.payments.pending)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-sm theme-text-secondary">
            {stripeError ? 'Erreur lors du chargement des statistiques Stripe' : 'Aucune donnée disponible'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

